import { Floor, Order, OrderProduct, Printer } from '../interfaces';
import { getBinding } from '../utils/printerBindings';

const formatDateForReceipt = (date: Date): string =>
    new Date(date).toLocaleString('en-US', {
        weekday: 'short',
        month: 'long',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
    });

export interface KitchenPrintArgs {
    order: Order | undefined;
    floor: Floor | null;
    /** Order-product ids to print (e.g. unsent items when sending, sent items when reprinting). */
    productIds: string[];
    /** Pre-fetched printers; if omitted the service fetches GET /Printer itself. */
    printers?: Printer[];
}

export interface KitchenPrintResult {
    /** true when the platform can print (Electron/native) and printing was attempted. */
    printed: boolean;
}

/**
 * Routes the given order products to their station printers (by category → printer
 * mapping) and prints a captain order to each. Pure print — it does NOT mark items
 * as sent. Shared by SendProductsPopup (initial send) and the live "Reprint kitchen
 * ticket" action so both follow one routing/printing path.
 *
 * On web (no Electron / not native) this is a no-op that resolves to { printed: false }.
 */
export async function printKitchenTickets({ order, floor, productIds, printers }: KitchenPrintArgs): Promise<KitchenPrintResult> {
    if (!order?.id || productIds.length === 0) return { printed: false };

    const { Capacitor } = await import('@capacitor/core');
    const canPrint = !!(window as any).electronAPI?.printThermalReceipt || Capacitor.isNativePlatform();
    if (!canPrint) return { printed: false };

    // Resolve printers if not provided.
    let resolvedPrinters: Printer[] = printers ?? [];
    if (!printers) {
        try {
            const r = await fetch(`${process.env.REACT_APP_API_URL}/Printer`, { method: 'GET', credentials: 'include' });
            resolvedPrinters = r.ok ? await r.json() : [];
        } catch {
            resolvedPrinters = [];
        }
    }

    // Group the requested products by their mapped station printer.
    const requested = (order.products ?? []).filter(p => productIds.includes(p.id));
    const groups: Record<string, OrderProduct[]> = {};
    requested.forEach(item => {
        const mapping = floor?.categoryPrinters?.find(cp => cp.productCategoryId === item.categoryId && cp.printerId);
        if (mapping?.printerId) {
            (groups[mapping.printerId] ||= []).push(item);
        }
    });

    const now = formatDateForReceipt(new Date());

    for (const [printerId, items] of Object.entries(groups)) {
        const fullPrinter = resolvedPrinters.find(p => p.id === printerId);
        if (!fullPrinter) continue;

        const ids = items.map(i => i.id);

        // Generate kitchen ticket HTML — API first, local fallback.
        let ticketUrl = '';
        let ticketHtmlContent = '';
        try {
            const genResponse = await fetch(`${process.env.REACT_APP_API_URL}/Order/generate-kitchen-order`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ order, productsToBePrintedIds: ids, formattedDate: now }),
                credentials: 'include',
            });
            if (!genResponse.ok) throw new Error('API failed');
            ticketUrl = await genResponse.text();
        } catch {
            const { default: ReceiptService } = await import('./ReceiptService');
            ticketHtmlContent = await ReceiptService.generateCaptainOrderHtml(order, now, ids);
        }

        // Bluetooth printers are bound to a local device on THIS machine (per-device
        // binding stored in localStorage). Resolve that binding before printing.
        const isBluetooth = (fullPrinter.connectionType || '').toLowerCase() === 'bluetooth';
        const binding = isBluetooth ? getBinding(fullPrinter.id) : null;
        if (isBluetooth && !binding) {
            console.error(`Bluetooth printer "${fullPrinter.name}" is not connected on this device — open Printer Settings to pair it.`);
            continue;
        }

        if ((window as any).electronAPI?.printThermalReceipt) {
            // On desktop a bound Bluetooth printer is a local serial (COM) port.
            const printerForPrint = isBluetooth && binding
                ? { ...fullPrinter, port: binding.deviceId }
                : fullPrinter;
            if (ticketUrl) {
                const result = await window.electronAPI.printThermalReceipt(ticketUrl, { ...order, printer: printerForPrint });
                if (!result?.success) console.error(`Print failed for ${fullPrinter.name}:`, result?.error);
            } else if (ticketHtmlContent) {
                const blob = new Blob([ticketHtmlContent], { type: 'text/html' });
                const blobUrl = URL.createObjectURL(blob);
                const result = await window.electronAPI.printThermalReceipt(blobUrl, { ...order, printer: printerForPrint });
                URL.revokeObjectURL(blobUrl);
                if (!result?.success) console.error(`Print failed for ${fullPrinter.name}:`, result?.error);
            }
        } else {
            try {
                const { ThermalPrinter } = (Capacitor as any).Plugins;
                if (!ThermalPrinter) { console.error('ThermalPrinter plugin not available'); continue; }
                await ThermalPrinter.printHtml({
                    htmlUrl: ticketUrl || undefined,
                    htmlContent: ticketHtmlContent || undefined,
                    // Native: Bluetooth uses the bound MAC address; otherwise TCP/IP.
                    ipAddress: isBluetooth ? undefined : fullPrinter.ipAddress,
                    bluetoothAddress: isBluetooth && binding ? binding.deviceId : undefined,
                    port: fullPrinter.port || 9100,
                    ticketWidthMm: fullPrinter.ticketWidth || 80,
                    autoCutter: fullPrinter.hasAutoCutter ?? true,
                    printerType: fullPrinter.type === 'Star Micronics' ? 'STAR' : 'EPSON',
                });
            } catch (e: any) {
                console.error(`Native print failed for ${fullPrinter.name}:`, e?.message || e);
            }
        }
    }

    return { printed: true };
}
